import React from 'react';
import { List, Avatar, Typography, Badge, Button } from 'antd';
import { UserOutlined, MenuFoldOutlined } from '@ant-design/icons';
import { Room } from '../../types/chat';
import { truncateMessageText, getMessageContent, stripHtmlTags } from './utils';
import NotificationSettings from './NotificationSettings';
import { useTranslation } from 'react-i18next';

const { Text, Title } = Typography;

interface RoomsListProps {
  rooms: Record<string, Room>;
  activeRoomId: string | null;
  onRoomSelect: (roomId: string) => void;
  toggleSidebar: () => void;
}

/**
 * Получает текст последнего сообщения в комнате
 */
const getLastMessageText = (room: Room): string => {
  if (!room.messages || room.messages.length === 0) {
    return 'Нет сообщений';
  }
  
  // Берем последнее сообщение
  const lastMessage = room.messages[room.messages.length - 1];
  
  // Получаем текст сообщения и удаляем HTML-теги
  const messageText = getMessageContent(lastMessage);
  
  return messageText || 'Пустое сообщение';
};

const RoomsList: React.FC<RoomsListProps> = ({ 
  rooms, 
  activeRoomId, 
  onRoomSelect, 
  toggleSidebar 
}) => {
  const { t } = useTranslation();
  // Обработчик клика по комнате
  const handleRoomClick = (roomId: string) => {
    // Вызываем обработчик выбора комнаты
    onRoomSelect(roomId);
    
    // Сохраняем информацию о взаимодействии в localStorage
    localStorage.setItem('user_interacted_with_rooms', 'true');
  };
  
  return (
    <>
      <div className="sidebar-header">
        <Title level={4} className="sidebar-title">{t('roomsList.chatRooms')}</Title>
        <Button 
          icon={<MenuFoldOutlined />} 
          type="text" 
          onClick={toggleSidebar}
          className="sidebar-toggle"
        />
      </div>
      <div className="rooms-list">
        <List
          dataSource={Object.entries(rooms)}
          renderItem={([id, room]) => (
            <List.Item
              className={`room-item ${activeRoomId === id ? 'active' : ''} ${room.hasNewMessage ? 'has-new-message' : ''}`}
              onClick={() => handleRoomClick(id)}
            >
              <Avatar
                style={{ backgroundColor: room.display_name ? '#f56a00' : '#87d068' }}
                icon={<UserOutlined />}
              />
              <div className="room-info">
                {room.display_name || id}
                <Text type="secondary" ellipsis className="room-description">
                  {truncateMessageText(getLastMessageText(room))}
                </Text>
              </div>
              
              <div className="room-actions">
                {room.hasNewMessage && (
                  <Badge count={<span className="new-message-badge" />} />
                )}
                <NotificationSettings roomId={id} />
              </div>
            </List.Item>
          )}
        />
      </div>
    </>
  );
};

export default RoomsList;